import React, { useMemo } from "react"
import { css } from "@emotion/react"
import { useHistory } from "react-router-dom"
import { Icon, TrackingEvent } from "@ncs/web-legos"
import { Button, Divider, Paragraph, PartImage } from "@ncs/web-legos"
import { useShopContext } from "~/contexts"
import { QuantityControl } from "~/shared-components/quantity-control/QuantityControl"
import { useAnalytics } from "~/hooks/useAnalytics"
import { ImagePartPreview } from "./ImagePartPreview"

export interface AddedToCartModalEcommProps {
	isOpen?: boolean
	onClose?: () => void
	partId?: string | null
	partIds?: string[]
}

export const AddedToCartModalEcomm: React.FC<AddedToCartModalEcommProps> = ({
	isOpen,
	onClose,
	partId,
	partIds,
}) => {
	const history = useHistory()
	const [{ cart }, shopDispatch] = useShopContext()
	const { trackEvent } = useAnalytics()

	const cartItems = useMemo(() => cart?.length, [cart])

	const cartParts = useMemo(() => {
		if (partId) {
			const id = String(partId)
			return cart.filter((c) => String(c.part.id) === id)
		}

		if (partIds) {
			return cart.filter((c) => partIds?.includes(c.part.id))
		}

		return []
	}, [partId, partIds, cart])

	if ((!partId && !partIds) || !cartParts || cartParts.length === 0) {
		return null
	}

	const onChangeQuantity = (selectedPartId: string, newQuantity: number) => {
		shopDispatch({
			type: "update part cart quantity",
			payload: {
				partId: selectedPartId,
				quantity: newQuantity,
			},
		})
	}

	const onRemove = (selectedPartId: string) => {
		shopDispatch({
			type: "remove from cart",
			payload: selectedPartId,
		})
	}

	const goToCart = () => {
		onClose?.()
		history.push("/shop/cart-summary")
		trackEvent(TrackingEvent.FC_VIEW_CART)
		trackEvent(TrackingEvent.GO_TO_CART)
	}

	return (
		<a
			css={overlay(isOpen)}
			href={"null"}
			onClick={(e) => {
				e.preventDefault()
				if (e.target === e.currentTarget) {
					onClose?.()
				}
			}}
		>
			<div css={cartModal}>
				<div css={productContainer}>
					<div css={header}>
						<Paragraph bold>ADDED TO CART</Paragraph>
						<Button onClick={onClose}>
							<Icon icon="close" fontSize={1.5} />
						</Button>
					</div>
					<Divider css={divider} />
					{cartParts.map(({ part, quantity }) => (
						<div css={row} key={part.id}>
							<div>
								<div css={imageBorder}>
									<ImagePartPreview
										src={part.imageUrl ?? ""}
										alt={part.title ?? ""}
										size="small"
										withPadding
									/>
								</div>
								<div>
									<Paragraph bold mb={0.5} mr={1}>
										{part.title}
									</Paragraph>
									<Paragraph customColor="#4B5563">
										#{part?.onlinePartNumber}
									</Paragraph>
								</div>
							</div>
							<QuantityControl
								value={quantity}
								onChange={(newQuantity) => onChangeQuantity(part.id, newQuantity)}
								onChooseZero={() => onRemove(part.id)}
								useUpdateButton
							/>
						</div>
					))}
				</div>
				<div css={button}>
					<Button customVariant="#0b75e1" onClick={goToCart}>
						VIEW CART ({cartItems})
					</Button>
				</div>
			</div>
		</a>
	)
}

const overlay = (isOpen: boolean | undefined) => css`
	right: 0;
	top: 0;
	position: absolute;
	left: 0;
	bottom: 0;
	display: ${isOpen ? "block" : "none"};
	z-index: 1;
	cursor: inherit;
`

const cartModal = css`
	width: 100%;
	max-width: 32rem;
	border-radius: 8px;
	border: 1px solid #e5e6e6;
	background: #ffffff;
	box-shadow: 0px 0px 4px 0px #d1d5db;
	padding: 1.5rem;
	position: fixed;
	right: 0.25rem;
	top: 0.25rem;
	z-index: 2;
	opacity: 1;
`

const header = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	button.MuiButtonBase-root path {
		width: 2rem;
		height: 2rem;
	}
`

const divider = css`
	margin-bottom: 1rem;
`

const productContainer = css`
	margin-bottom: 1.5rem;
`

const imageBorder = css`
	margin-right: 1rem;

	img {
		display: block;
	}
`

const row = css`
	display: flex;
	justify-content: space-between;
	align-items: space-between;
	> div:first-of-type {
		display: flex;
		margin-right: 1rem;
	}
`

const button = css`
	display: flex;
	justify-content: center;
	.button-text {
		font-size: 0.875rem;
	}
`
